/**
 * Branch Map Component
 * Interactive map showing all branch locations with markers and popups
 */

import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Branch } from '../stores/branch-store';

// Fix Leaflet default icon issue in webpack/Vite
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Configure default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface BranchMapProps {
  branches: Branch[];
  className?: string;
  onError?: () => void;
}

// Component to fit map bounds to markers
function MapBoundsHandler({ branches }: { branches: Branch[] }) {
  const map = useMap();
  
  useEffect(() => {
    const validBranches = branches.filter(b => b.lat != null && b.lng != null);
    if (validBranches.length > 0) {
      const bounds = L.latLngBounds(
        validBranches.map(b => [b.lat!, b.lng!] as [number, number])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [branches, map]);
  
  return null;
}

export function BranchMap({ branches, className = '', onError }: BranchMapProps) {
  const [mapError, setMapError] = useState<string | null>(null);
  
  // Filter branches with valid coordinates
  const validBranches = branches.filter(b => b.lat != null && b.lng != null);
  
  // Default center: Nairobi, Kenya
  const defaultCenter: [number, number] = [-1.2921, 36.8219];
  
  // Calculate center from branches or use default
  const center: [number, number] = validBranches.length > 0
    ? [
        validBranches.reduce((sum, b) => sum + (b.lat || 0), 0) / validBranches.length,
        validBranches.reduce((sum, b) => sum + (b.lng || 0), 0) / validBranches.length
      ]
    : defaultCenter;

  if (mapError) {
    return (
      <div className={`bg-brand-50 rounded-lg p-6 text-center ${className}`}>
        <MapPin className="w-12 h-12 text-brand-400 mx-auto mb-3" />
        <p className="text-brand-600 mb-3">{mapError}</p>
        <Button 
          variant="outline" 
          onClick={() => setMapError(null)}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg overflow-hidden border border-brand-200 ${className}`}
      role="application"
      aria-label="Branch locations map"
    >
      <MapContainer
        center={center}
        zoom={11}
        style={{ height: '100%', minHeight: '300px' }}
        className="z-0"
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapBoundsHandler branches={branches} />
        
        {validBranches.map((branch) => (
          <Marker
            key={branch.id}
            position={[branch.lat!, branch.lng!]}
          >
            <Popup>
              <div className="p-1 min-w-[220px]">
                <h3 className="font-bold text-brand-900 text-base mb-2">
                  {branch.name}
                  {branch.is_main && (
                    <span className="ml-2 text-xs bg-brand-600 text-white px-1.5 py-0.5 rounded">
                      Main
                    </span>
                  )}
                </h3>
                
                <div className="space-y-1.5 text-sm">
                  <p className="text-brand-700 flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    <span>{branch.location}</span>
                  </p>
                  
                  {branch.phone && (
                    <p className="text-brand-700 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      <a 
                        href={`tel:${branch.phone.replace(/\s+/g, '')}`}
                        className="hover:text-brand-600 hover:underline"
                      >
                        {branch.phone}
                      </a>
                    </p>
                  )}
                  
                  {branch.operating_hours && (
                    <p className="text-brand-700 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      <span>
                        {branch.operating_hours.open} - {branch.operating_hours.close}
                      </span>
                    </p>
                  )}
                  
                  {branch.lat && branch.lng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 hover:underline mt-2"
                    >
                      <Navigation className="w-4 h-4" />
                      Get Directions
                    </a>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export function BranchMapSkeleton({ className = '' }: { className?: string }) {
  return (
    <div 
      className={`bg-brand-100 rounded-lg animate-pulse ${className}`}
      style={{ height: '400px', minHeight: '300px' }}
    >
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-brand-300 mx-auto mb-2" />
          <p className="text-brand-400 text-sm">Loading map...</p>
        </div>
      </div>
    </div>
  );
}
